import { generateID, asyncTimeout } from '@caresend/utils';
import { defineComponent, ref, computed, reactive, onMounted, onUnmounted, set } from 'vue';
import __vue_component__ from '../../alerts/Pane/Pane.vue.js';
import __vue_component__$1 from '../InteractivePopup/components/TransitionOnResize.vue.js';
import { propSync, delayedComputed, useElementDimensions, useMediaQuery, getElementAncestor } from '../../../composables/dom.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../router/helpers/initRouter.js';
import '../Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import { preventParentClickAndDefault } from '../../../functions/preventDefault.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';

var script = defineComponent({
    name: 'SuperPopover',
    components: {
        Pane: __vue_component__,
        TransitionOnResize: __vue_component__$1,
    },
    model: {
        prop: 'isOpened',
        event: 'update',
    },
    props: {
        /**
         * If true, the popover will be positioned relative to the click position
         * instead of the trigger element.
         */
        useInteractionPosition: {
            type: Boolean,
            default: false,
        },
        containerComponent: {
            type: [String, Object],
            default: 'div',
        },
        /**
         * Being that the entire popover is hoisted to the root(ish) of the DOM,
         * we need to be able to add a class to the container
         */
        containerClass: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        isOpened: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        isBottomBoundMobile: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { emit }) {
        const componentID = ref(generateID());
        const triggerSlot = ref(null);
        const trigger = ref(null);
        const isOpen = propSync(() => props.isOpened, (value) => emit('update', value));
        /** Used to allow the popover to be rendered while the transition is in progress */
        const isOpenProxy = delayedComputed(() => props.isOpened, 150);
        const { top: triggerTop, height: triggerHeight, left: triggerLeft, } = useElementDimensions(triggerSlot, {
            forceObserveRef: isOpen,
            resize: true,
            debug: true,
        });
        const getRootBody = () => {
            const container = document.getElementById(`${componentID.value}`);
            const rootBody = container ? getElementAncestor(container, '.App')
                || document.getElementsByClassName('App')[0] : null;
            return rootBody;
        };
        const getViewportDimensions = () => {
            // Get viewport dimensions
            const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
            const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
            return {
                width: viewportWidth,
                height: viewportHeight,
            };
        };
        const contentDimensions = ref({
            width: 0,
            height: 0,
        });
        const interactionPosition = ref({
            x: 0,
            y: 0,
        });
        /** "Clamps" a value between a minimum and maximum value */
        const clamp = (value, { min, max, }) => Math.min(Math.max(value, min), max);
        const getOutOfBoundsCompensation = (left, top, offset) => {
            const { width: screenWidth, height: screenHeight, } = getViewportDimensions();
            // Ensure content stays within viewport bounds
            const adjustedLeft = clamp(left, {
                min: 0,
                max: Math.max(0, screenWidth - contentDimensions.value.width - offset),
            });
            const adjustedTop = clamp(top, {
                min: 0,
                max: Math.max(0, screenHeight - contentDimensions.value.height - offset),
            });
            return {
                x: adjustedLeft,
                y: adjustedTop,
            };
        };
        const contentPosition = computed(() => {
            let top = 0;
            let left = 0;
            const offset = 4;
            if (props.useInteractionPosition) {
                top = Math.round(interactionPosition.value.y + offset);
                left = Math.round(interactionPosition.value.x + offset);
            }
            else {
                top = Math.round(triggerTop.value + triggerHeight.value + offset);
                left = Math.round(triggerLeft.value + offset);
            }
            // Get adjusted position to keep content in bounds
            const adjusted = getOutOfBoundsCompensation(left, top, offset);
            return {
                transform: `translate(${adjusted.x}px, ${adjusted.y}px)`,
            };
        });
        const classNames = computed(() => ({
            'SuperPopover--disabled': props.disabled,
        }));
        const containerStyles = reactive({
            pointerEvents: 'none',
            opacity: 0,
        });
        const cleanSelf = async (timeout = 0) => {
            await asyncTimeout(timeout);
            const container = document.getElementById(`${componentID.value}`);
            if (container) {
                if (trigger.value)
                    trigger.value.appendChild(container);
                else
                    container.remove();
            }
            componentID.value = generateID();
        };
        const { matches: isMobile } = useMediaQuery('mobile');
        const toggleOpenState = async (e) => {
            if (e) {
                preventParentClickAndDefault(e);
                interactionPosition.value = {
                    x: e.clientX,
                    y: e.clientY,
                };
            }
            if (isMobile.value) {
                isOpen.value = !isOpen.value;
            }
            else if (!isOpen.value) {
                isOpen.value = true;
                set(containerStyles, 'opacity', 0);
                set(containerStyles, 'zIndex', 2);
                set(containerStyles, 'pointerEvents', 'auto');
                /** Grant JS a runtime tick to apply styles to DOM  */
                await asyncTimeout(0);
                /** Move the popover to the root */
                const container = document.getElementById(`${componentID.value}`);
                const rootBody = getRootBody();
                /** Move the popover to the root */
                if (container && rootBody) {
                    rootBody.appendChild(container);
                }
                /** Grant JS a runtime tick for the transition to complete  */
                await asyncTimeout(0);
                /** Set the opacity to 1 */
                set(containerStyles, 'opacity', 1);
            }
            else {
                /** Set the opacity to 0 */
                // set(containerStyles, 'zIndex', -100);
                set(containerStyles, 'pointerEvents', 'none');
                set(containerStyles, 'opacity', 0);
                // const container = document.getElementById(`${componentID}`);
                // const background = document.getElementById(`${componentID}-background`);
                // const rootBody = getRootBody();
                /** Grant JS a runtime tick for the transition to complete  */
                await cleanSelf(150);
                isOpen.value = false;
            }
        };
        // Add event listener for ESC key
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && isOpen.value) {
                toggleOpenState();
            }
        };
        onMounted(() => {
            window.addEventListener('keydown', handleEscKey);
            /**
             * Safari's implementation of table elements does not allow for position: relative to be applied.
             * The hack in the CSS below is a workaround for the actual trigger to be in the right place,
             * but this ensures that it is the right height
             *
             * This is currently only applied to table rows. In the event other container components need this,
             * we can update this to check for those container components or add a prop to toggle this behavior.
             */
            if (trigger.value && triggerSlot.value && props.containerComponent === 'tr') {
                const { height } = trigger.value.getBoundingClientRect();
                triggerSlot.value.style.height = `${height}px`;
            }
        });
        onUnmounted(() => {
            window.removeEventListener('keydown', handleEscKey);
            if (isOpen.value)
                toggleOpenState();
            cleanSelf();
        });
        return {
            componentID,
            classNames,
            contentDimensions,
            contentPosition,
            containerStyles,
            isMobile,
            isOpen,
            isOpenProxy,
            trigger,
            triggerSlot,
            preventParentClickAndDefault,
            onClick: toggleOpenState,
        };
    },
});

export { script as default };
